import { useState } from "react";
import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { FormContextData } from "@thalamos/aspire-common";

import { api } from "~/api.js";
import {
  Banner,
  BannerList,
  Button,
  Dropdown,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
  renderSuccessToast,
  TextField,
} from "~/components/design-system/index.js";
import { routeFns } from "~/routePaths.js";

export interface ReasonOption {
  value: string;
  label: string;
}

const availableReasons: ReasonOption[] = [
  { value: "amend-required", label: "An amend is required" },
  { value: "file-note-required", label: "A file note is required" },
  { value: "other", label: "Other" },
];

export function UnfinaliseDialog({
  onClose,
  reloadFormContext,
  formContext,
}: {
  formContext: Omit<FormContextData, "patient">;

  onClose: () => void;
  reloadFormContext: () => void;
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [selectedReasons, setSelectedReasons] = useState<string>("");
  const [additionalInfo, setAdditionalInfo] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canContinue = !isSubmitting && !!selectedReasons && !!additionalInfo;

  const submitFn = async () => {
    setIsSubmitting(true);

    const reason = availableReasons.find(
      (reason) => reason.value === selectedReasons,
    )?.label;

    if (!reason) {
      return;
    }

    try {
      const res = await api.work.unfinalise(
        formContext.activeTeamworkWorkItem!.id,
        {
          reason,
          reasonDescription: additionalInfo,
        },
      );

      if (res.status !== 204) {
        renderErrorToast({
          message: "Could not undo marking legal scrutiny complete",
        });
      } else {
        renderSuccessToast({ message: "Legal scrutiny successfully undone" });
        reloadFormContext();
        navigate(
          routeFns.formContextPage(formContext.id, formContext.patientId),
        );
      }
    } catch (e) {
      renderErrorToast({
        message:
          (e as any)?.response?.data?.reason ??
          "Could not undo marking legal scrutiny complete",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PopupDialog open={true} onClose={onClose}>
      <PopupDialogTitle
        titleText={t("pages.formPage.unfinaliseAdmission.title")}
        closeDialog={onClose}
      />
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: theme.spacing(2) }}
      >
        <Banner
          bannerType={BannerList.WARNING}
          title={t("pages.formPage.unfinaliseAdmission.warning")}
        />
        <Dropdown
          label={t("pages.formPage.unfinaliseAdmission.reasonLabel")}
          name="reason"
          values={availableReasons}
          selectedValue={selectedReasons}
          onChange={(e) => {
            setSelectedReasons(e);
          }}
        />
        <TextField
          name="additionalInfo"
          label="Please provide additional information"
          onChange={(e) => setAdditionalInfo(e)}
          value={additionalInfo}
          useFullWidth={true}
          multiline
          rows="5"
        />
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            marginTop: theme.spacing(3),
          }}
        >
          <Button label={"Close"} onClick={onClose} variant="outlined" />
          <Button
            color="primary"
            disabled={!canContinue}
            label={"Confirm"}
            onClick={() => {
              void submitFn();
            }}
          />
        </Box>
      </Box>
    </PopupDialog>
  );
}
